import { createBrowserHistory } from "history";

// HistoryRouter uses the installed history package to support browser back/forward blocking.
export const appHistory = createBrowserHistory();

// Leave guards: every part of a page with unsaved work registers one entry while it is dirty
// (or saving). A single history blocker serves all entries (history's `block` also adds the
// browser's beforeunload prompt), so two dirty sections on one page ask once, not twice.
// Entry: { label, message, isBusy, onBusy, confirm, guardsPlantSwitch }
// - `confirm` (legacy) fully replaces the prompt for that entry.
// - `guardsPlantSwitch`: whether switching the selected warehouse would discard this entry's work
//   (only then does the warehouse switcher ask about it). Legacy entries always guard it.
// - `isBusy()` true → navigation is refused and `onBusy()` is called (e.g. a toast).
// - one dirty entry → its own `message`; several → one combined prompt naming each `label`.
const guards = new Map();
let nextGuardId = 0;
let unblock = null;
let stopListening = null;

function joinLabels(labels) {
	if (labels.length <= 1) return labels.join("");
	return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

export function confirmLeave({ plantSwitch = false } = {}) {
	const entries = [...guards.values()].filter((entry) => !plantSwitch || entry.guardsPlantSwitch !== false);
	if (!entries.length) return true;
	const busy = entries.find((entry) => entry.isBusy?.());
	if (busy) {
		busy.onBusy?.();
		return false;
	}
	if (entries.length === 1) {
		const [entry] = entries;
		if (entry.confirm) return entry.confirm();
		return window.confirm(entry.message || "Discard unsaved changes and leave?");
	}
	const labels = entries.map((entry) => entry.label).filter(Boolean);
	return window.confirm(labels.length ? `You have unsaved changes in ${joinLabels(labels)}. Discard and leave?` : "Discard unsaved changes and leave?");
}
// Kept for existing callers (tabs, scope switch): same check, covering every registered guard.
export const confirmThemeLeave = () => confirmLeave();
// Warehouse switcher: asks only about work that a warehouse change would actually discard.
export const confirmPlantSwitch = () => confirmLeave({ plantSwitch: true });

function startBlocking() {
	if (unblock) return;
	unblock = appHistory.block((transaction) => {
		if (!confirmLeave()) return;
		stopBlocking();
		// POP retries finish asynchronously. Re-block only after the accepted transition completes.
		stopListening = appHistory.listen(() => {
			stopListening();
			stopListening = null;
			if (guards.size) startBlocking();
		});
		transaction.retry();
	});
}

function stopBlocking() {
	if (!unblock) return;
	unblock();
	unblock = null;
}

export function registerLeaveGuard(entry) {
	const id = ++nextGuardId;
	guards.set(id, entry);
	startBlocking();
	return () => {
		if (!guards.delete(id) || guards.size) return;
		stopBlocking();
		stopListening?.();
		stopListening = null;
	};
}

// Legacy single-function form: `guard()` returns true to allow leaving.
export function registerThemeLeaveGuard(guard) {
	return registerLeaveGuard({ confirm: guard });
}
